import axios from 'axios';
import { ISearchRecord } from '../types/search';
import { applyPlotLatLong, isValidLatLon } from './favoriteApi.util';

export type EntityLocationPayload = ISearchRecord['location'];

type FetchEntityLocationOptions = {
  /** When true, includes geom_json (can be >1 MB). Default false. */
  includeGeomJson?: boolean;
};

/** Fetch facility coordinates from query_ceres_ed via REST (lightweight by default). */
export const fetchEntityLocation = async (
  entityNumber: string,
  options: FetchEntityLocationOptions = {}
): Promise<EntityLocationPayload | null> => {
  const trimmed = String(entityNumber || '').trim();
  if (!trimmed) {
    return null;
  }

  const response = await axios.post(`${process.env.REACT_APP_CERES_URL}/getEntityLocation`, {
    data: {
      query: {
        entityNumber: trimmed,
        includeGeomJson: options.includeGeomJson === true,
      },
    },
  });

  const location = response.data?.result?.location;
  if (!location || typeof location !== 'object') {
    return null;
  }
  return location as EntityLocationPayload;
};

/** Merge DB location onto a record and decrypt geomz when needed. */
export const mergeEntityLocationOntoRecord = (
  record: ISearchRecord,
  location: EntityLocationPayload
): ISearchRecord =>
  applyPlotLatLong({
    ...record,
    location: {
      ...record.location,
      ...location,
    },
  });

/**
 * Resolve coordinates for a record: use existing lat/lon, else await query_ceres_ed.
 */
export const resolveRecordLocation = async (
  record: ISearchRecord,
  options: FetchEntityLocationOptions = {}
): Promise<ISearchRecord> => {
  const withLocal = applyPlotLatLong(record);
  if (isValidLatLon(withLocal.location.lat, withLocal.location.lon)) {
    return withLocal;
  }

  const entityNumber = withLocal.entityNumber;
  if (!entityNumber) {
    return withLocal;
  }

  try {
    const location = await fetchEntityLocation(entityNumber, options);
    if (!location) {
      return withLocal;
    }
    return mergeEntityLocationOntoRecord(withLocal, location);
  } catch {
    return withLocal;
  }
};

/** Whether local record already has EPN map-point geometry (geom_json), not just site pin (geomz). */
export const hasEpnGeometry = (record: ISearchRecord): boolean => {
  const geomJson = record.location?.geom_json;
  return !!geomJson && typeof geomJson === 'object' && Object.keys(geomJson).length > 0;
};

/**
 * Load EPN geometry from query_ceres_ed when geom_json / geomz is not on the record yet.
 * Uses the full payload (can be >1 MB) — only call when plotting EPN markers.
 */
export const resolveRecordEpnGeometry = async (
  record: ISearchRecord
): Promise<ISearchRecord> => {
  const withLocal = applyPlotLatLong(record);
  if (hasEpnGeometry(withLocal)) {
    return withLocal;
  }

  const entityNumber = withLocal.entityNumber;
  if (!entityNumber) {
    return withLocal;
  }

  try {
    const location = await fetchEntityLocation(entityNumber, { includeGeomJson: true });
    if (!location) {
      return withLocal;
    }
    return mergeEntityLocationOntoRecord(withLocal, location);
  } catch {
    return withLocal;
  }
};
